import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import {
  Button, Modal, ModalBody, ModalHeader, Progress,
} from 'reactstrap';
import styled from 'styled-components';

const LoadProgress = styled(Progress).attrs({
  className: 'my-2 rounded-pill',
})`
  height: 1.5rem;
`;

const LoadProgressBar = styled(Progress).attrs({
  className: 'rounded-pill',
})``;

const PercentageDisplay = styled.div.attrs({
  className: 'text-muted small',
})`
  white-space: nowrap;
  margin-left: 0.5rem;
`;

const ProgressLabelText = styled.div.attrs({
  className: 'mt-2',
})``;

/**
 * @typedef {object} LoadingOverlayInputs
 * @property {string} bodyLabel - The text to display in the modal body.
 * @property {string} footerLabel - The text to display in the modal footer.
 * @property {string} headerLabel - The text to display in the modal header.
 * @property {string} [progressLabel] - The text to display below the progress bar.
 * @property {number} [progressBarValue=50] - The number of periods(chunks) already loaded.
 * @property {Function} updateProgress - A callback function to be called when the modal opens.
 * @property {Function} [onCancelFunc] - Optional callback to cancel the loading operation.
 * @property {string} [cancelLabel] - Label for the cancel button.
 */

/**
 * LoadingOverlay component displays a modal with a loading progress bar.
 * It is designed to block user interaction during a loading process.
 * @param {object} props - The component's props.
 * @param {LoadingOverlayInputs} props.inputs - An object containing all the inputs
 *  to configure the loading overlay.
 * @returns {React.ReactElement|null} The LoadingOverlay modal component or null.
 */
function LoadingOverlay({ inputs }) {
  if (!inputs || !inputs.headerLabel || !inputs.bodyLabel || !inputs.updateProgress) {
    return null;
  }
  // If the loaded value(periodsLoaded) is not present, the progress will be shown at the 50% range.
  const {
    bodyLabel, footerLabel, headerLabel, progressLabel,
    updateProgress, progressBarValue = 50,
    onCancelFunc, cancelLabel,
  } = inputs;
  const modalRef = useRef(null);

  const handleModalOpened = () => {
    try {
      updateProgress(true);
    } catch (error) {
      console.error('Error in updateProgress callback:', error);
    }

    if (modalRef.current) {
      modalRef.current.focus();
    }
  };

  const showPercentage = progressBarValue != null;

  return (
    <Modal
      isOpen
      innerRef={modalRef}
      backdrop="static"
      keyboard={false}
      centered
      zIndex={10000} // to ensure it appears above the DatePicker
      onOpened={handleModalOpened}
    >
      <ModalHeader>
        <h5 className="modal-title">{headerLabel}</h5>
      </ModalHeader>
      <ModalBody>
        <span>{bodyLabel}</span>
        <div className="d-flex align-items-center">
          <LoadProgress multi className="flex-grow-1">
            <LoadProgressBar
              animated
              bar
              value={progressBarValue}
              color="primary"
            />
          </LoadProgress>
          {showPercentage && (
            <PercentageDisplay>
              {progressBarValue}
              %
            </PercentageDisplay>
          )}
        </div>
        {progressLabel && <ProgressLabelText>{progressLabel}</ProgressLabelText>}
        {footerLabel && <p>{footerLabel}</p>}
        {onCancelFunc && cancelLabel && (
          <div className="d-flex justify-content-end mt-3">
            <Button color="primary" onClick={onCancelFunc}>
              {cancelLabel}
            </Button>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}

export default LoadingOverlay;

LoadingOverlay.propTypes = {
  inputs: PropTypes.shape({
    bodyLabel: PropTypes.string.isRequired,
    footerLabel: PropTypes.string,
    headerLabel: PropTypes.string.isRequired,
    progressBarValue: PropTypes.number,
    progressLabel: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
    updateProgress: PropTypes.func.isRequired,
    onCancelFunc: PropTypes.func,
    cancelLabel: PropTypes.string,
  }),
};

LoadingOverlay.defaultProps = {
  inputs: {
    footerLabel: null,
    progressBarValue: 50,
    progressLabel: null,
    onCancelFunc: null,
    cancelLabel: null,
  },
};
